import { useAuth } from "@vellum/micro-app-auth";
import { Alert, Button, Dialog, TextInput } from "@vellum/ui";
import {
  UserRoles,
  type MaintenanceAppStatus,
  type MaintenanceStatus,
} from "@vellum/vellum-api";
import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";

// The configured SDK, not @vellum/vellum-api directly — importing this module
// is what points the client at the API origin with credentials.
import { vellumApiSdk } from "@vellum/micro-app-auth";

// Keys come from `maintenanceApps` in pulumi/app/maintenance.ts; anything new
// there shows up here under its key until it gets a label.
const APP_LABELS: Record<string, string> = {
  luna: "Luna (Peak Mobile)",
  customer: "Customer Platform",
};

function appLabel(app: string): string {
  return APP_LABELS[app] ?? app;
}

export function MaintenancePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === UserRoles.ADMIN;

  const [status, setStatus] = useState<MaintenanceStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [pending, setPending] = useState<MaintenanceAppStatus | null>(null);
  const [confirmation, setConfirmation] = useState("");
  const [saving, setSaving] = useState(false);
  const [writeFailed, setWriteFailed] = useState(false);

  const loadStatus = useCallback(async () => {
    const result = await vellumApiSdk.getMaintenanceStatus();
    setStatus(result.data ?? null);
    setFailed(result.data === undefined);
  }, []);

  useEffect(() => {
    if (!isAdmin) {
      return;
    }

    let active = true;
    void (async () => {
      try {
        await loadStatus();
      } catch {
        if (active) {
          setFailed(true);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [isAdmin, loadStatus]);

  const closeDialog = () => {
    setPending(null);
    setConfirmation("");
  };

  const confirmToggle = async () => {
    if (!pending) {
      return;
    }

    setSaving(true);
    try {
      // The generated SDK resolves with an `error` instead of throwing.
      const result = await vellumApiSdk.setMaintenance({
        body: { enabled: !pending.enabled },
        path: { app: pending.app },
      });
      if (result.error !== undefined) {
        setWriteFailed(true);
        return;
      }

      await loadStatus();
      setWriteFailed(false);
      closeDialog();
    } catch {
      setWriteFailed(true);
    } finally {
      setSaving(false);
    }
  };

  const enabling = pending !== null && !pending.enabled;
  // Taking an app down needs the environment typed back; bringing it back is a
  // plain confirm.
  const canConfirm =
    !enabling ||
    confirmation.trim().toLowerCase() === (status?.environment ?? "");

  // The API also rejects non-admins; the redirect just keeps them off the page.
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div>
      <h1 className="mb-2 text-3xl font-bold tracking-tight text-green-700">
        Maintenance mode
      </h1>
      <p className="mb-7 max-w-xl text-sm leading-relaxed text-gray-600">
        Turning an app on shows everyone the maintenance page (HTTP 503) within
        seconds, except IPs on the bypass list. It stays on across deploys until
        someone turns it off here or with <code>tools/maintenance.sh</code>.
        This hub and its API are never affected.
      </p>

      {writeFailed ? (
        <Alert className="mb-4">
          That change did not apply. Check your connection and try again.
        </Alert>
      ) : null}

      {loading ? (
        <p className="text-sm text-gray-500">Loading maintenance status…</p>
      ) : failed || !status ? (
        <p className="text-sm text-gray-500">
          We could not load maintenance status just now. Refresh the page to try
          again.
        </p>
      ) : status.apps.length === 0 ? (
        <p className="text-sm text-gray-500">
          No apps are configured for maintenance mode in this environment.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-xs font-semibold uppercase tracking-wide text-gray-500">
                <th className="py-2 pr-4">App</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {status.apps.map((app) => (
                <tr
                  key={app.app}
                  className="border-b border-gray-100 align-top"
                >
                  <td className="py-3 pr-4 text-gray-800">
                    {appLabel(app.app)}
                    <span className="ml-2 font-mono text-xs text-gray-400">
                      {app.app}
                    </span>
                  </td>
                  <td className="py-3 pr-4">
                    <span
                      className={
                        app.enabled
                          ? "inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700"
                          : "inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700"
                      }
                    >
                      {app.enabled ? "Maintenance page on" : "Serving traffic"}
                    </span>
                  </td>
                  <td className="py-3 pr-4">
                    <div className="flex justify-end">
                      <Button
                        variant="outline"
                        className={
                          app.enabled
                            ? undefined
                            : "border-red-300 text-error hover:bg-red-50"
                        }
                        onClick={() => setPending(app)}
                      >
                        {app.enabled ? "Turn off" : "Turn on"}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog
        isOpen={pending !== null}
        onClose={saving ? () => {} : closeDialog}
        title={
          pending
            ? `Turn maintenance ${enabling ? "on" : "off"} for ${appLabel(pending.app)}?`
            : ""
        }
        icon={enabling ? "iconoir-warning-triangle" : "iconoir-check"}
        iconColor={enabling ? "red" : "green"}
      >
        {enabling ? (
          <>
            <p className="mt-2 text-sm text-gray-600">
              Everyone on <strong>{status?.environment}</strong> will see the
              maintenance page immediately, including users mid-task. Type the
              environment name to confirm.
            </p>
            <TextInput
              containerClassName="mt-4"
              id="maintenance-confirmation"
              autoComplete="off"
              placeholder={status?.environment ?? ""}
              value={confirmation}
              disabled={saving}
              onChange={(event) => setConfirmation(event.target.value)}
            />
          </>
        ) : (
          <p className="mt-2 text-sm text-gray-600">
            Traffic resumes immediately.
          </p>
        )}
        <div className="mt-6 flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            disabled={saving}
            onClick={closeDialog}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant={enabling ? "danger" : "primary"}
            loading={saving}
            disabled={saving || !canConfirm}
            onClick={() => void confirmToggle()}
          >
            {enabling ? "Turn on maintenance" : "Turn off maintenance"}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
